/**
 * web/src/lib/findPlaceholders.ts
 *
 * Detects unfilled single-brace placeholders (e.g. `{Registration Deadline}`,
 * `{OPS CONTACTS}`, `{Location}`) left in an email body — the convention
 * Pixel-team doc authors use to mark "human, fill this in." Used by the
 * EmailPreview overlay (highlighting) and by GateReview's pre-send
 * confirmation modal (warning + explicit acknowledgement).
 *
 * The regex deliberately mirrors the matcher in
 * src/workflows/sponsor/templates/single-brace-substitution.ts so the UI
 * highlights exactly what the server-side substitution failed to fill.
 *
 * Excludes:
 *   - Double-brace `{{var}}` (Mustache — substituted upstream)
 *   - Empty braces `{}`
 *   - Braces spanning newlines
 *   - Very long matches (likely JSON / accidental brace pairs, capped at 80 chars)
 */

/**
 * Matches `{placeholder}` where placeholder is non-empty, has no nested
 * braces, no newlines, and is short enough to plausibly be a placeholder
 * (not a stray JSON object literal). Mirrors the substitution-side regex.
 */
export const PLACEHOLDER_REGEX = /(?<!\{)\{([^{}\n]{1,80})\}(?!\})/g;

/** One placeholder match — used by both highlight overlay and warning list. */
export interface Placeholder {
  /** The literal text including braces, e.g. "{Registration Deadline}". */
  match: string;
  /** Inner key (placeholder name without braces), trimmed. */
  key: string;
  /** Start index in the source string (inclusive). */
  start: number;
  /** End index in the source string (exclusive). */
  end: number;
}

/**
 * Finds every unfilled single-brace placeholder in a body string.
 *
 * @param body the rendered email body (post-Mustache, post-LLM)
 * @returns ordered list of matches by position; empty when none
 * @sideEffects none — pure function
 * @idempotent yes
 */
export function findPlaceholders(body: string): Placeholder[] {
  const out: Placeholder[] = [];
  for (const m of body.matchAll(PLACEHOLDER_REGEX)) {
    if (m.index === undefined) continue;
    const inner = (m[1] ?? '').trim();
    if (inner.length === 0) continue;
    out.push({
      match: m[0],
      key: inner,
      start: m.index,
      end: m.index + m[0].length,
    });
  }
  return out;
}

/**
 * Deduplicates a placeholder list by key (case-insensitive, whitespace-collapsed)
 * so the warning UI shows each unique placeholder once even if it appears
 * multiple times in the body.
 */
export function uniquePlaceholderKeys(placeholders: Placeholder[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const p of placeholders) {
    const norm = p.key.trim().toLowerCase().replace(/\s+/g, ' ');
    if (seen.has(norm)) continue;
    seen.add(norm);
    out.push(p.key);
  }
  return out;
}
